import { DEBUG_NAMESPACE, useDebug } from '@/composables/useDebug';
import { defineStore } from 'pinia';
import { useClipboard } from '@vueuse/core';
import { useToast } from '@/composables/useToast';

const insightsZipFilename = 'insights.zip';
const OCR_ARTIFACT_TYPE = 'Ocr';

export enum ArtifactStatus {
  PENDING = 'pending',
  COMPLETED = 'completed',
}

export interface PublicBucketUrl {
  filename: string;
  url: string;
}

export interface Artifact {
  id: string;
  type: string;
  status: ArtifactStatus;
  createdAt: string;
  updatedAt: string;
  content: string | null;
  publicBucketUrls: PublicBucketUrl[];
  itemId: string;
}

export interface OcrBoundingBox {
  top: number;
  left: number;
  width: number;
  height: number;
}

export interface OcrWord {
  text: string;
  confidence: number;
  boundingBox: OcrBoundingBox;
}

export interface OcrLine {
  text: string;
  confidence: number;
  boundingBox: OcrBoundingBox;
  words?: OcrWord[];
}

export interface OcrData {
  lines: OcrLine[];
  fullText: string;
}

export interface ArtifactStoreState {
  list: Artifact[];
  selectionMode: boolean;
  selectedLineIndices: number[];
}

const { debug } = useDebug(DEBUG_NAMESPACE.STORE_ARTIFACTS);

// Parse the OCR artifact content JSON string into OcrData.
// The backend stores content with camelCase keys matching the frontend interfaces.
const parseOcrContent = (content: string | null): OcrData | null => {
  if (!content) {
    return null;
  }
  try {
    return JSON.parse(content) as OcrData;
  } catch {
    debug('[parseOcrContent] Failed to parse OCR content');
    return null;
  }
};

export const useArtifactsStore = defineStore('artifactsStore', {
  state: () =>
    ({
      list: [],
      selectionMode: false,
      selectedLineIndices: [],
    }) as ArtifactStoreState,
  actions: {
    initialize(artifacts: any[]) {
      debug('[initialize] Initialize Artifacts store', artifacts);
      if (artifacts && artifacts.length > 0) {
        const mappedArtifacts = artifacts.map(artifact => ({
          id: artifact.data.id,
          type: artifact.data.attributes.type,
          status: artifact.data.attributes.status,
          createdAt: artifact.data.attributes.createdAt,
          updatedAt: artifact.data.attributes.updatedAt,
          content: artifact.data.attributes.content,
          publicBucketUrls: artifact.data.attributes.publicBucketUrls || [],
          itemId: artifact.data.relationships?.item?.data?.id || '',
        }));
        this.setArtifacts(mappedArtifacts);
      }
    },
    setArtifacts(artifacts: Artifact[]) {
      if (!artifacts || artifacts.length === 0) {
        debug('Cannot set artifacts data', artifacts);
        return;
      }

      this.$patch({ list: artifacts });
    },
    // Upsert a single artifact (used for Pusher updates).
    upsertArtifact(artifact: Artifact) {
      const index = this.list.findIndex(a => a.id === artifact.id);
      if (index >= 0) {
        this.list[index] = artifact;
      } else {
        this.list.push(artifact);
      }
    },
    enterSelectionMode() {
      this.selectionMode = true;
      this.selectedLineIndices = [];
    },
    exitSelectionMode() {
      this.selectionMode = false;
      this.selectedLineIndices = [];
    },
    toggleLineSelection(index: number) {
      const pos = this.selectedLineIndices.indexOf(index);
      if (pos === -1) {
        this.selectedLineIndices.push(index);
      } else {
        this.selectedLineIndices.splice(pos, 1);
      }
    },
    copySelectedText() {
      const data = this.ocrData;
      const lines = data?.lines ?? [];
      const text = this.selectedLineIndices
        .slice()
        .sort((a, b) => a - b)
        .map(i => lines[i]?.text ?? '')
        .join('\n');

      const { copy } = useClipboard();
      const { success } = useToast();
      copy(text);
      success('Text copied to clipboard', { icon: 'general.copy' });
      this.exitSelectionMode();
    },
    copyAllText() {
      const text = this.ocrFullText;
      const { copy } = useClipboard();
      const { success } = useToast();
      copy(text);
      success('Text copied to clipboard', { icon: 'general.copy' });
    },
  },
  getters: {
    insightsURL: (state): string =>
      state.list
        .filter(
          ({ type, status, publicBucketUrls }) =>
            type === 'Insights' && status === ArtifactStatus.COMPLETED && publicBucketUrls.some(({ filename }) => filename === insightsZipFilename)
        )
        .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
        .at(0)
        ?.publicBucketUrls.find(({ filename }) => filename === insightsZipFilename)?.url || '',

    ocrArtifact: (state): Artifact | undefined => state.list.find(a => a.type === OCR_ARTIFACT_TYPE),

    ocrData(): OcrData | null {
      const artifact = this.ocrArtifact;
      if (!artifact) {
        return null;
      }
      return parseOcrContent(artifact.content);
    },

    hasOcrData(): boolean {
      const data = this.ocrData;
      return !!(data && data.lines && data.lines.length > 0);
    },

    ocrFullText(): string {
      return this.ocrData?.fullText ?? '';
    },

    ocrLines(): OcrLine[] {
      return this.ocrData?.lines ?? [];
    },

    isOcrProcessing(): boolean {
      const artifact = this.ocrArtifact;
      if (!artifact) {
        return false;
      }
      return artifact.status === ArtifactStatus.PENDING;
    },

    isOcrDone(): boolean {
      const artifact = this.ocrArtifact;
      if (!artifact) {
        return false;
      }
      return artifact.status === ArtifactStatus.COMPLETED;
    },

    selectedCount: (state): number => state.selectedLineIndices.length,
    hasSelection: (state): boolean => state.selectedLineIndices.length > 0,
  },
});
